import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/lib/api/client'
import { isApiError } from '@/lib/api/errors'
import type { Resource, SignInStep, Staff } from '@/lib/api/types'

export const meKey = ['me'] as const

/** The signed-in staff member, or null when signed out. */
export function useMe() {
  return useQuery({
    queryKey: meKey,
    queryFn: async () => {
      try {
        return (await http.get<Resource<Staff>>('/me')).data
      } catch (error) {
        if (isApiError(error) && error.status === 401) return null
        throw error
      }
    },
    staleTime: 60_000,
  })
}

export function useSignIn() {
  return useMutation({
    mutationFn: async (input: { email: string; password: string }) =>
      (await http.post<Resource<SignInStep>>('/auth/sign-in', input)).data,
  })
}

export function useTwoFactor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { code?: string; recovery_code?: string }) =>
      (
        await http.post<Resource<{ staff: Staff; recovery_codes: string[] | null }>>(
          '/auth/two-factor',
          input,
        )
      ).data,
    onSuccess: (data) => queryClient.setQueryData(meKey, data.staff),
  })
}

export function useSignOut() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => http.post('/auth/sign-out'),
    onSettled: () => {
      queryClient.clear()
      queryClient.setQueryData(meKey, null)
    },
  })
}
